import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle2, ExternalLink } from 'lucide-react';
import { usePageMeta } from '@/lib/usePageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/strategy-intensive`;

// ---------------- CHECKOUT GATE ----------------
export function StrategyIntensivePay() {
  const { token } = useParams();
  const [gate, setGate] = useState(null);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  usePageMeta({ title: 'Complete Your Strategy Intensive Purchase | Toia Wallace', path: `/strategy-intensive/pay`, robots: 'noindex, follow' });

  useEffect(() => {
    axios.get(`${API}/pay/${token}`)
      .then((r) => setGate(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This payment link is no longer valid.'));
  }, [token]);

  useEffect(() => {
    if (gate?.already_paid && gate?.schedule_token) {
      window.location.replace(`/strategy-intensive/schedule/${gate.schedule_token}`);
    }
  }, [gate]);

  const proceed = async () => {
    if (!agreed) { toast.error('Please check the Purchase Agreement to continue.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pay/${token}`, {
        agreement_accepted: true,
        // Client also echoes back the versions it displayed (server verifies against its own state of truth)
        policy_versions_seen: gate?.policy_versions || {},
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not create checkout session.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment gateway error.');
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="bg-ivory min-h-[70vh] pt-32 sm:pt-40 lg:pt-56 px-6 sm:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="font-serif-display text-3xl sm:text-4xl text-matte">This link is no longer valid.</h1>
          <p className="mt-4 text-matte/70">{error}</p>
          <Link to="/contact" className="btn-primary mt-8">Contact the Studio<ArrowRight size={14} strokeWidth={1.6} /></Link>
        </div>
      </div>
    );
  }
  if (!gate) {
    return <div className="bg-ivory min-h-[70vh] pt-40 px-6 sm:px-10 text-center text-taupe">Loading…</div>;
  }
  if (gate.already_paid) {
    return <div className="bg-ivory min-h-[70vh] pt-40 px-6 text-center text-taupe">Redirecting to your session scheduling…</div>;
  }

  const price = (gate.price_cents / 100).toFixed(0);

  return (
    <div className="bg-ivory" data-testid="si-pay-gate">
      <section className="pt-32 sm:pt-40 lg:pt-52 pb-20 lg:pb-32">
        <div className="mx-auto max-w-[820px] px-6 sm:px-10">
          <div className="eyebrow text-taupe mb-6">Secure Your Session · Private Link</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tightest text-matte">
            You&rsquo;re in, {(gate.name || '').split(' ')[0]}.
            <br />
            <em className="italic text-taupe">Let&rsquo;s book you.</em>
          </h1>

          <div className="mt-10 sm:mt-12 bg-white border border-taupe/20 rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-8 pb-6 border-b border-taupe/15">
              <div>
                <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-2">Order Summary</div>
                <div className="font-serif-display text-2xl sm:text-3xl text-matte leading-tight">{gate.offer_title}</div>
                <div className="text-matte/70 text-sm mt-2">{gate.duration_minutes} minutes · one-on-one with Toia</div>
              </div>
              <div className="font-serif-display text-4xl sm:text-5xl text-matte tabular-nums">${price}</div>
            </div>

            <div className="mt-6">
              <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">Purchase Agreement</div>
              <p className="text-matte/80 text-[14px] leading-[1.75] mb-4">
                By checking this box, I confirm that I am purchasing the 90-minute Strategy Intensive with Toia Wallace for $297.
                I have read, understand, and agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline decoration-gold underline-offset-4 text-matte hover:text-gold">Terms &amp; Conditions</Link>,{' '}
                <Link to="/no-refund-policy" target="_blank" rel="noopener noreferrer" className="underline decoration-gold underline-offset-4 text-matte hover:text-gold">No Refund Policy</Link>, and{' '}
                <Link to="/rescheduling-cancellation-policy" target="_blank" rel="noopener noreferrer" className="underline decoration-gold underline-offset-4 text-matte hover:text-gold">Rescheduling &amp; Cancellation Policy</Link>.
                I understand that all sales are final and non-refundable, subject to applicable law.
              </p>
              <label className="flex items-start gap-3 cursor-pointer p-3 sm:p-4 bg-ivory rounded-lg border border-taupe/15 hover:border-gold/40 transition-colors">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="si-purchase-agreement-checkbox"
                  className="mt-1 w-4 h-4 accent-gold shrink-0"
                />
                <span className="text-matte text-[13px] sm:text-[14px] leading-snug">
                  I have read, understand, and agree to the Purchase Agreement, Terms &amp; Conditions, and No-Refund Policy.
                </span>
              </label>
            </div>

            <button
              onClick={proceed}
              disabled={!agreed || busy}
              data-testid="si-pay-continue"
              className="btn-primary mt-8 w-full justify-center bg-matte text-ivory hover:bg-[#1a1a1a] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Redirecting to Stripe…' : `Continue to Secure Payment · $${price}`}
              <ArrowRight size={14} strokeWidth={1.6} />
            </button>
            <p className="text-taupe text-[11px] mt-4 text-center">Payments processed securely by Stripe. Your card details never touch our servers.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------- SCHEDULE PAGE ----------------
export function StrategyIntensiveSchedule() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const sessionId = params.get('session_id');

  usePageMeta({ title: 'Schedule Your Strategy Intensive | Toia Wallace', path: '/strategy-intensive/schedule', robots: 'noindex, follow' });

  const load = () => axios
    .get(`${API}/schedule/${token}`, { params: sessionId ? { session_id: sessionId } : {} })
    .then((r) => setData(r.data))
    .catch((e) => setError(e?.response?.data?.detail || 'Unable to load your scheduling page.'));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, sessionId]);

  if (error) {
    return (
      <div className="bg-ivory min-h-[70vh] pt-40 px-6 sm:px-10 text-center">
        <h1 className="font-serif-display text-3xl text-matte">Scheduling unavailable</h1>
        <p className="mt-4 text-matte/70 max-w-lg mx-auto">{error}</p>
      </div>
    );
  }
  if (!data) return <div className="bg-ivory min-h-[70vh] pt-40 text-center text-taupe">Loading…</div>;
  if (!data.paid) {
    return (
      <div className="bg-ivory min-h-[70vh] pt-40 px-6 text-center max-w-xl mx-auto">
        <h1 className="font-serif-display text-3xl text-matte">Waiting on payment confirmation…</h1>
        <p className="mt-4 text-matte/70">{data.message || 'Your payment is still confirming. This page will unlock as soon as Stripe reports success.'}</p>
        <button onClick={load} className="btn-primary mt-8">Refresh</button>
      </div>
    );
  }

  return (
    <div className="bg-ivory" data-testid="si-schedule">
      <section className="pt-32 sm:pt-40 lg:pt-52 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[900px] px-6 sm:px-10">
          <div className="eyebrow text-taupe mb-6">Payment Confirmed · Book Your Session</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tightest text-matte">
            Choose your 90 minutes.
          </h1>
          <p className="mt-6 text-matte/70 max-w-lg leading-relaxed">
            Pick a time that fits your calendar. You&rsquo;ll get an instant confirmation from Cal.com with the video call link.
          </p>

          {data.scheduled_at ? (
            <div className="mt-10 bg-white border border-taupe/20 rounded-2xl p-6 sm:p-8">
              <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-2">Your Session Is Booked</div>
              <div className="font-serif-display text-3xl sm:text-4xl text-matte mb-3">
                {new Date(data.scheduled_at).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
              {data.cal_meeting_url && (
                <a href={data.cal_meeting_url} target="_blank" rel="noopener noreferrer" className="btn-primary mt-4">
                  Join Meeting <ExternalLink size={14} strokeWidth={1.6} />
                </a>
              )}
              {data.prep_token && (
                <div className="mt-8 pt-8 border-t border-taupe/15">
                  <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-2">Prep Questionnaire</div>
                  <p className="text-matte/70 mb-4 text-[14px]">Complete this before your session so we can make the most of our 90 minutes.</p>
                  <Link to={`/strategy-intensive/prep/${data.prep_token}`} className="btn-ghost" data-testid="si-schedule-prep-link">
                    {data.prep_status === 'submitted' ? 'Review Your Answers' : data.prep_status === 'in_progress' ? 'Continue Prep' : 'Start Prep Questionnaire'}
                    <ArrowRight size={14} strokeWidth={1.6} />
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-8 rounded-2xl overflow-hidden border border-taupe/20 bg-white">
              <iframe
                title="Cal.com scheduling"
                src={data.cal_com_url}
                className="w-full"
                style={{ minHeight: '780px', border: '0' }}
                data-testid="si-cal-iframe"
              />
              <div className="p-4 text-taupe text-[12px] border-t border-taupe/15">
                Trouble seeing the calendar?{' '}
                <a href={data.cal_com_url} target="_blank" rel="noopener noreferrer" className="underline decoration-gold underline-offset-4">Open Cal.com in a new tab</a>
                . Once you book, refresh this page to see your confirmation.
              </div>
            </div>
          )}

          {data.confirmation_message && (
            <p className="mt-8 text-matte/70 text-[14px] italic max-w-2xl">{data.confirmation_message}</p>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------- PREP QUESTIONNAIRE ----------------
export function StrategyIntensivePrep() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  usePageMeta({ title: 'Prep Questionnaire | Toia Wallace', path: '/strategy-intensive/prep', robots: 'noindex, follow' });

  useEffect(() => {
    axios.get(`${API}/prep/${token}`)
      .then((r) => {
        setData(r.data);
        const initial = {};
        (r.data.questions || []).forEach((q) => { initial[q.id] = q.answer || ''; });
        setAnswers(initial);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Unable to load your prep form.'));
  }, [token]);

  const save = async (submit) => {
    setBusy(true);
    try {
      await axios.post(`${API}/prep/${token}`, { answers, submit });
      toast.success(submit ? 'Answers submitted. Thank you.' : 'Progress saved.');
      // Reload
      const r = await axios.get(`${API}/prep/${token}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  if (error) return <div className="bg-ivory min-h-[70vh] pt-40 px-6 text-center text-matte">{error}</div>;
  if (!data) return <div className="bg-ivory min-h-[70vh] pt-40 text-center text-taupe">Loading…</div>;

  const submitted = data.status === 'submitted';

  return (
    <div className="bg-ivory" data-testid="si-prep">
      <section className="pt-32 sm:pt-40 lg:pt-52 pb-20 lg:pb-32">
        <div className="mx-auto max-w-[820px] px-6 sm:px-10">
          <div className="eyebrow text-taupe mb-6">Strategy Intensive · Prep</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tightest text-matte">
            Prepare for our
            <br />
            <em className="italic text-taupe">session.</em>
          </h1>
          <p className="mt-6 text-matte/70 max-w-xl leading-relaxed">
            Answer honestly and thoroughly — the more context I have going in, the more we can accomplish in 90 minutes. Your progress saves as you go.
          </p>

          <div className="mt-4 uppercase tracking-[0.22em] text-[10px] text-taupe">
            Status: <span className={submitted ? 'text-emerald-700' : 'text-gold'}>{data.status.replace('_', ' ')}</span>
          </div>

          <div className="mt-10 space-y-6">
            {data.questions.map((q) => (
              <label key={q.id} className="block">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-serif-display text-lg sm:text-xl text-matte leading-tight">
                    {q.order}. {q.question}
                  </span>
                  {q.required && <span className="text-gold text-[10px] uppercase tracking-[0.2em]">required</span>}
                </div>
                <textarea
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                  disabled={submitted}
                  rows={3}
                  data-testid={`si-prep-q-${q.id}`}
                  className="w-full bg-white border border-taupe/20 rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-gold resize-y disabled:bg-taupe/5"
                />
              </label>
            ))}
          </div>

          {!submitted && (
            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={() => save(false)} disabled={busy} className="btn-ghost">Save Progress</button>
              <button onClick={() => save(true)} disabled={busy} className="btn-primary" data-testid="si-prep-submit">
                {busy ? 'Submitting…' : 'Submit Prep'}
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
            </div>
          )}
          {submitted && (
            <div className="mt-10 flex items-center gap-3 text-emerald-700">
              <CheckCircle2 size={18} strokeWidth={1.6} />
              <span>Submitted on {data.submitted_at ? new Date(data.submitted_at).toLocaleString() : ''}. See you soon.</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
